import React, { useImperativeHandle, useRef, useState } from "react";
import { RCCGrid, RCCHookToast, RCCRefModalProps, RCCRefSpreadsheetProps, RCCServicesApi, RCCSwitch } from "react-common-components";
import Modal from "src/components/Modal";
import { useTranslation } from "src/context/Translations/context";
import { standardErrorMessages } from "src/utils/errors";
import { useLoaderBackdrop } from "src/context/LoaderBackdrop/context";
import { useForm } from "react-hook-form";
import LovBudgetPlanYears from "src/components/Lov/BudgetPlanYears";
import Spreadsheet from "src/components/Spreadsheet";
import formatDate from "src/utils/formatDate";
import { addMonths } from "date-fns";
import stringToNumber from "src/utils/stringToNumber";
import LovSubsidiaryLastLevel from "src/components/Lov/SubsidiaryLastLevel";
import formatNumber from "src/utils/formatNumber";
import IModalRefCenterCostFinancialManagement from "./interface";


const ModalCenterCost: React.ForwardRefRenderFunction<IModalRefCenterCostFinancialManagement> = (_, ref) => {
	const { translate, currentLanguage } = useTranslation(); 
	const { addToast } = RCCHookToast();
	const { setLoading } = useLoaderBackdrop();
    
	const { handleSubmit, control, register, reset, setValue, watch } = useForm();

	const budgetId = useRef<number>(0);
	const budgetPlanId = useRef<number>(0);
	const modalCostCenter = useRef<RCCRefModalProps>(null);
	const [subtitleModal, setSubtitleModal] = useState("");
	const onCallbackFunction = useRef<Function>(() => {});
	const spreadsheetRef = useRef<RCCRefSpreadsheetProps>(null);
	const [spreadsheetValues, setSpreadsheetValues] = useState<string[][]>([[]]);
	const [hideSpreadsheet, setHideSpreadsheet] = useState(true);
	const [centerCostCorporative, setCenterCostCorporative] = useState<boolean>(false);
	const startMonthRef = useRef<number>(1);
	const [headerDate, setHeaderDate] = useState(new Date());
	const [isTotalCC, setIsTotalCC] = useState(false);
	const [subsidiaryDefaultValue, setSubsidiaryDefaultValue] = useState<string | null>(null);
	const openModalCenterCost = async (id: number, subtitle: string, ofa001Id: number, isTotal: boolean, onCallback?: Function) => {
		try{
			reset();
			setLoading(true);
			budgetId.current = id;
			budgetPlanId.current = ofa001Id;
			setHideSpreadsheet(true);
			setIsTotalCC(isTotal);

			const {data} = await RCCServicesApi.getRequest({
				method: "CostCenter/IsCorporativeAndStartMonth",
				urlParameters: {
					id,
					budgetPlanId: ofa001Id
				}
			});
			
			startMonthRef.current = data.startMonth;
			setCenterCostCorporative(data.corporative);
			setSubsidiaryDefaultValue(data.subsidiary !== null ? String(data.subsidiary) : null);
			setSubtitleModal(subtitle);
			onCallbackFunction.current = onCallback || (() => {});
			
			setLoading(false);
			modalCostCenter.current?.openModal();

			if(data.subsidiary !== null)
				window.setTimeout(() => {
					onChangeLovPeriods({
						period: watch("period"),
						corporative: data.corporative,
						subsidiary: String(data.subsidiary)
					});
				}, 500);

		}catch (err){
			setLoading(false);
			standardErrorMessages(err, translate("Erro ao buscar dados"), addToast);
		}
	};
	const onChangeLovPeriods = async (data: any) => {
		if(!data.period || data.period === null || data.period.length === 0 || 
		   !data.subsidiary || data.subsidiary === null || data.subsidiary.length === 0){
			setHideSpreadsheet(true);
			return;
		}
		try{
			setHideSpreadsheet(false);
			setLoading(true);

			const response = await RCCServicesApi.getRequest({
				method: "CostCenter/GetApportionmentSpreadsheet",
				urlParameters: {
					id: budgetId.current,
					budgetPlanId: budgetPlanId.current,
					currentLanguage,
					...data,
					copyPeriod: data.copyPeriod !== undefined ? data.copyPeriod : ""
				}
			});

			const newDate = new Date(Number(data.period), startMonthRef.current - 1, 1);		
			setHeaderDate(newDate);

			setSpreadsheetValues(response.data);
			
			
			setLoading(false);
		}catch (err){
			setLoading(false);
			standardErrorMessages(err, translate("Erro ao buscar dados"), addToast);
		}
	};
	const onSubmit = async (data: any) => {
		modalCostCenter.current?.setLoadingButton();
		let newValuesMonthlyData: string[][] = [];
		if(isTotalCC && data.subsidiary === null){
			addToast({
				title: translate("Erro"),
				description: translate("Unidade/Filial não selecionada"),
				color: "yellow"
			}); 
			modalCostCenter.current?.resetLoadingButton(); 
			return;
		}
		if(!hideSpreadsheet && spreadsheetValues.length > 0){
			newValuesMonthlyData = spreadsheetRef.current ? spreadsheetRef.current.getData() as string[][] : [];
			newValuesMonthlyData.pop();
			const lengthOfSpreadsheet = newValuesMonthlyData.length;
			const totalValue2 = stringToNumber(spreadsheetRef.current?.getLabelFromCoords(2, lengthOfSpreadsheet) ?? "", currentLanguage);
			const totalValue3 = stringToNumber(spreadsheetRef.current?.getLabelFromCoords(3, lengthOfSpreadsheet) ?? "", currentLanguage);
			const totalValue4 = stringToNumber(spreadsheetRef.current?.getLabelFromCoords(4, lengthOfSpreadsheet) ?? "", currentLanguage);
			const totalValue5 = stringToNumber(spreadsheetRef.current?.getLabelFromCoords(5, lengthOfSpreadsheet) ?? "", currentLanguage);
			const totalValue6 = stringToNumber(spreadsheetRef.current?.getLabelFromCoords(6, lengthOfSpreadsheet) ?? "", currentLanguage);
			const totalValue7 = stringToNumber(spreadsheetRef.current?.getLabelFromCoords(7, lengthOfSpreadsheet) ?? "", currentLanguage);
			const totalValue8 = stringToNumber(spreadsheetRef.current?.getLabelFromCoords(8, lengthOfSpreadsheet) ?? "", currentLanguage);
			const totalValue9 = stringToNumber(spreadsheetRef.current?.getLabelFromCoords(9, lengthOfSpreadsheet) ?? "", currentLanguage);
			const totalValue10 = stringToNumber(spreadsheetRef.current?.getLabelFromCoords(10, lengthOfSpreadsheet) ?? "", currentLanguage);
			const totalValue11 = stringToNumber(spreadsheetRef.current?.getLabelFromCoords(11, lengthOfSpreadsheet) ?? "", currentLanguage);
			const totalValue12 = stringToNumber(spreadsheetRef.current?.getLabelFromCoords(12, lengthOfSpreadsheet) ?? "", currentLanguage);
			const totalValue13 = stringToNumber(spreadsheetRef.current?.getLabelFromCoords(13, lengthOfSpreadsheet) ?? "", currentLanguage);
			if((totalValue13 !== 100 && totalValue13 > 0) || 
			   (totalValue2 !== 100 && totalValue2 > 0) || 
			   (totalValue3 !== 100 && totalValue3 > 0) || 
			   (totalValue4 !== 100 && totalValue4 > 0) || 
			   (totalValue5 !== 100 && totalValue5 > 0) || 
			   (totalValue6 !== 100 && totalValue6 > 0) || 
			   (totalValue7 !== 100 && totalValue7 > 0) || 
			   (totalValue8 !== 100 && totalValue8 > 0) || 
			   (totalValue9 !== 100 && totalValue9 > 0) || 
			   (totalValue10 !== 100 && totalValue10 > 0) || 
			   (totalValue11 !== 100 && totalValue11 > 0) || 
			   (totalValue12 !== 100 && totalValue12 > 0)
			){
				addToast({
					title: translate("Erro"),
					description: translate("Valor total deve ser igual a 100"),
					color: "yellow"
				}); 
				modalCostCenter.current?.resetLoadingButton(); 
				return;
			}
		}
		try{	

			await RCCServicesApi.postRequest({
				method: "CostCenter/InsertApportionment",
				body: {
					id: budgetId.current,
					budgetPlanId: budgetPlanId.current,
					currentLanguage,
					...data,
					period: data.period !== undefined ? data.period : "",
					spreadsheet: {
						data: newValuesMonthlyData.map((line) => line.map((item) => String(item)))
					},
					subsidiaryId: data.subsidiary?.length > 0 ? data.subsidiary : null
				}
			});

			addToast({
				color: "green",
				description: translate("Rateios adicionados com sucesso"),
				title: translate("Sucesso")
			});
			onCallbackFunction.current();
			modalCostCenter.current?.resetLoadingButton();
			modalCostCenter.current?.closeModal();
		}catch(err){
			modalCostCenter.current?.resetLoadingButton();
			standardErrorMessages(err, translate("Erro ao salvar rateios"), addToast);
		}
	};

	useImperativeHandle(ref, () => ({
		openModalCenterCost
	}));


	return (
		<Modal
			ref={modalCostCenter}
			header={{
				title: translate("Rateio de centro de custo"),
				subtitle: subtitleModal,
				icon: {
					name: "refresh"
				}
			}}
			onSubmit={handleSubmit(onSubmit)}
		>
			
			<RCCGrid container item style={isTotalCC ? {display: "none"} : {}}>
				<RCCSwitch 
					name="corporative"
					register={register}
					label={translate("Centro de custo corporativo")}
					defaultChecked={isTotalCC || centerCostCorporative}
					disabled={isTotalCC}
					onChangeCapture={(e: any) => {
						setCenterCostCorporative(e.target.checked);
						setHideSpreadsheet(true);
					}}			
				/>
			</RCCGrid>
			{(centerCostCorporative || isTotalCC) && <>
				<LovSubsidiaryLastLevel 
					control={control}
					name="subsidiary"
					label={translate("Unidade/Filial")}
					extraParametersOnLoad={{
						budgetPlanId: budgetPlanId.current
					}}
					defaultValue={subsidiaryDefaultValue}
					isClearable
					onChangeCapture={handleSubmit(onChangeLovPeriods)}
				/>
				<RCCGrid container item spacing={1}>
					<RCCGrid container item xs={12} sm={6}>
						<LovBudgetPlanYears 
							control={control}
							name="period"
							label={translate("Período")}
							setValue={setValue}
							extraParametersOnLoad={{
								budgetPlanId: budgetPlanId.current
							}}
							onChangeCapture={handleSubmit(onChangeLovPeriods)}
							isClearable
						/>
					</RCCGrid>
					<RCCGrid container item xs={12} sm={6}>
						<LovBudgetPlanYears 
							control={control}
							name="copyPeriod"
							label={`${translate("Copiar")} ${translate("Período")}`}
							extraParametersOnLoad={{
								budgetPlanId: budgetPlanId.current
							}}
							onChangeCapture={handleSubmit(onChangeLovPeriods)}
							isClearable
						/>
					</RCCGrid>
				</RCCGrid>
				<br />
				{!hideSpreadsheet && spreadsheetValues.length > 0 && (
					<Spreadsheet 
						ref={spreadsheetRef}
						data={[
							...spreadsheetValues,
							[translate("Total"), "",
								`=ROUND(SUM(C1:C${spreadsheetValues.length}), 2)`, `=ROUND(SUM(D1:D${spreadsheetValues.length}), 2)`, 
								`=ROUND(SUM(E1:E${spreadsheetValues.length}), 2)`, `=ROUND(SUM(F1:F${spreadsheetValues.length}), 2)`, 
								`=ROUND(SUM(G1:G${spreadsheetValues.length}), 2)`, `=ROUND(SUM(H1:H${spreadsheetValues.length}), 2)`, 
								`=ROUND(SUM(I1:I${spreadsheetValues.length}), 2)`, `=ROUND(SUM(J1:J${spreadsheetValues.length}), 2)`, 
								`=ROUND(SUM(K1:K${spreadsheetValues.length}), 2)`, `=ROUND(SUM(L1:L${spreadsheetValues.length}), 2)`, 
								`=ROUND(SUM(M1:M${spreadsheetValues.length}), 2)`, `=ROUND(SUM(N1:N${spreadsheetValues.length}), 2)`
							]
						]}
						editable
						columns={[
							{ type: "text", title: translate("Unidade/Filial"), width: 420, readOnly: true, wordWrap: true },
							{ type: "hidden", title: "", width: 0 },
							{ type: "numeric", title: formatDate(headerDate, currentLanguage, false, "dd/"), width: 120,  decimal: "," },
							{ type: "numeric", title: formatDate(addMonths(headerDate, 1), currentLanguage, false, "dd/"), width: 120,  decimal: "," },
							{ type: "numeric", title: formatDate(addMonths(headerDate, 2), currentLanguage, false, "dd/"), width: 120,  decimal: "," },
							{ type: "numeric", title: formatDate(addMonths(headerDate, 3), currentLanguage, false, "dd/"), width: 120,  decimal: "," },
							{ type: "numeric", title: formatDate(addMonths(headerDate, 4), currentLanguage, false, "dd/"), width: 120,  decimal: "," },
							{ type: "numeric", title: formatDate(addMonths(headerDate, 5), currentLanguage, false, "dd/"), width: 120,  decimal: "," },
							{ type: "numeric", title: formatDate(addMonths(headerDate, 6), currentLanguage, false, "dd/"), width: 120,  decimal: "," },
							{ type: "numeric", title: formatDate(addMonths(headerDate, 7), currentLanguage, false, "dd/"), width: 120,  decimal: "," },
							{ type: "numeric", title: formatDate(addMonths(headerDate, 8), currentLanguage, false, "dd/"), width: 120,  decimal: "," },
							{ type: "numeric", title: formatDate(addMonths(headerDate, 9), currentLanguage, false, "dd/"), width: 120,  decimal: "," },
							{ type: "numeric", title: formatDate(addMonths(headerDate, 10), currentLanguage, false, "dd/"), width: 120,  decimal: "," },
							{ type: "numeric", title: formatDate(addMonths(headerDate, 11), currentLanguage, false, "dd/"), width: 120,  decimal: "," }
						]}
						readonlyLastRows={1}
						format={{
							columns: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13],
							functionToFormat: (number: string | number) => {
								if(number === null || number === undefined || number === "" || String(number).trim().length === 0)
									return "";
								return formatNumber(number, currentLanguage, 2);
							}
						}}
						onEditionEnd={(instance?: any, cell?: any, col?: any, row?: any, val?: any) => {
							if(val === 0)
								spreadsheetRef.current?.setValueFromCoords(col, row, "", true);
						}}
						showButtons={false}
					/>
				)}
			</>}
		</Modal>
	);
};

export default React.forwardRef(ModalCenterCost);
